// VoiLog 案件ダッシュボード — 共有プリミティブ（Claude Design プロトタイプ移植 / TypeScript 化）
//
// すべて tokens.css の CSS 変数を参照する。元プロトタイプ
// (_management/designs/ui_prototype/app/primitives.jsx) のコンポーネント構造を
// そのまま再現する。アイコンは lucide-react（プロトタイプは lucide UMD）。
import React from 'react';
import {
  GitCommitHorizontal, Video, Building, MapPin, Clock, User, Gavel, Check,
  CircleDot, Minus, Users, GitCompare, AlertCircle, RotateCcw, ArrowRight,
  Sparkles, Sparkle, Info, RefreshCw, Loader, ArrowLeft, MessageSquareText,
  Search, ChevronRight, Mic, ArrowUpDown, FolderSearch, X, CircleHelp,
  ListChecks, ArrowUpRight, ArrowUp, CornerDownRight, Calendar, Share2,
  Download, AlignLeft, CalendarClock, Play, Pause, Lock, ArrowUpCircle,
  WifiOff, LayoutGrid, Filter, Folder, Palette, Monitor, Copy,
  LucideIcon,
} from 'lucide-react';

// ─── Lucide アイコン登録（kebab 名 → コンポーネント。tree-shake 可能） ───
// プロトタイプは data-lucide のケバブ名で指定していたため、同じ呼び出し感覚を保つ。
const ICONS: Record<string, LucideIcon> = {
  'git-commit-horizontal': GitCommitHorizontal,
  'video': Video,
  'building': Building,
  'map-pin': MapPin,
  'clock': Clock,
  'user': User,
  'gavel': Gavel,
  'check': Check,
  'circle-dot': CircleDot,
  'minus': Minus,
  'users': Users,
  'git-compare': GitCompare,
  'alert-circle': AlertCircle,
  'rotate-ccw': RotateCcw,
  'arrow-right': ArrowRight,
  'sparkles': Sparkles,
  'sparkle': Sparkle,
  'info': Info,
  'refresh-cw': RefreshCw,
  'loader': Loader,
  'arrow-left': ArrowLeft,
  'message-square-text': MessageSquareText,
  'search': Search,
  'chevron-right': ChevronRight,
  'mic': Mic,
  'arrow-up-down': ArrowUpDown,
  'folder-search': FolderSearch,
  'x': X,
  'circle-help': CircleHelp,
  'list-checks': ListChecks,
  'arrow-up-right': ArrowUpRight,
  'arrow-up': ArrowUp,
  'corner-down-right': CornerDownRight,
  'calendar': Calendar,
  'share-2': Share2,
  'download': Download,
  'align-left': AlignLeft,
  'calendar-clock': CalendarClock,
  'play': Play,
  'pause': Pause,
  'lock': Lock,
  'arrow-up-circle': ArrowUpCircle,
  'wifi-off': WifiOff,
  'layout-grid': LayoutGrid,
  'filter': Filter,
  'folder': Folder,
  'palette': Palette,
  'monitor': Monitor,
  'copy': Copy,
};

export type IconName = keyof typeof ICONS | string;

interface IconProps {
  name: IconName;
  size?: number;
  stroke?: number;
  spin?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export function Icon({ name, size = 20, stroke = 1.5, spin, style, className }: IconProps) {
  const Cmp = ICONS[name] || CircleHelp;
  const cls = [spin ? 'vl-spin' : '', className || ''].filter(Boolean).join(' ') || undefined;
  return (
    <span className={cls} style={{ display: 'inline-flex', lineHeight: 0, ...style }} aria-hidden>
      <Cmp size={size} strokeWidth={stroke} />
    </span>
  );
}

// ─── ロゴマーク（棒グラフ） ───
export function LogoMark({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="vlg" x1="0" y1="48" x2="0" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#00D2BE" />
          <stop offset="1" stopColor="#FFAA28" />
        </linearGradient>
      </defs>
      <rect x="6" y="26" width="6" height="16" rx="3" fill="#00D2BE" />
      <rect x="15" y="18" width="6" height="24" rx="3" fill="#00D2BE" />
      <rect x="24" y="10" width="6" height="32" rx="3" fill="url(#vlg)" />
      <rect x="33" y="22" width="6" height="20" rx="3" fill="#FFAA28" />
    </svg>
  );
}

// ─── プライマリ / セカンダリ ボタン ───
type BtnKind = 'cta' | 'accent' | 'solid' | 'ghost';
interface BtnProps {
  children?: React.ReactNode;
  kind?: BtnKind;
  size?: 'sm' | 'md';
  icon?: IconName;
  iconSpin?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  title?: string;
}

export function Btn({ children, kind = 'ghost', size = 'md', icon, iconSpin, onClick, disabled, style, title }: BtnProps) {
  const base: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    font: 'var(--type-ui)', cursor: disabled ? 'not-allowed' : 'pointer',
    borderRadius: 'var(--r-pill)', whiteSpace: 'nowrap',
    transition: 'all var(--dur-fast) var(--ease)',
    opacity: disabled ? 0.4 : 1,
    padding: size === 'sm' ? '7px 14px' : '10px 18px',
  };
  const kinds: Record<BtnKind, React.CSSProperties> = {
    cta: { background: 'var(--cta)', color: 'var(--on-cta)', border: '1px solid transparent', fontWeight: 600 },
    accent: { background: 'var(--accent-fill)', color: 'var(--accent)', border: '1px solid var(--border-accent)' },
    solid: { background: 'var(--bg-hover)', color: 'var(--fg)', border: '1px solid var(--border-strong)' },
    ghost: { background: 'transparent', color: 'var(--fg-secondary)', border: '1px solid var(--border-strong)' },
  };
  return (
    <button onClick={disabled ? undefined : onClick} disabled={disabled} title={title}
      style={{ ...base, ...kinds[kind], ...style }}
      onMouseEnter={(e) => { if (!disabled && kind === 'cta') e.currentTarget.style.background = 'var(--cta-hover)'; }}
      onMouseLeave={(e) => { if (!disabled && kind === 'cta') e.currentTarget.style.background = 'var(--cta)'; }}>
      {icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} spin={iconSpin} />}
      {children}
    </button>
  );
}

// ─── ステータスドット付きピル ───
type PillKind = 'teal' | 'amber' | 'success' | 'danger' | 'neutral';
export function Pill({ kind = 'neutral', children, pulse }: { kind?: PillKind; children: React.ReactNode; pulse?: boolean }) {
  const map: Record<PillKind, { bg: string; fg: string; dot: string }> = {
    teal: { bg: 'var(--accent-fill)', fg: 'var(--teal-300)', dot: 'var(--teal-400)' },
    amber: { bg: 'var(--cta-fill)', fg: 'var(--amber-400)', dot: 'var(--amber-500)' },
    success: { bg: 'var(--success-fill)', fg: 'var(--success)', dot: 'var(--success)' },
    danger: { bg: 'var(--danger-fill-strong)', fg: 'var(--danger)', dot: 'var(--danger)' },
    neutral: { bg: 'var(--overlay-soft)', fg: 'var(--fg-tertiary)', dot: 'var(--fg-tertiary)' },
  };
  const s = map[kind] || map.neutral;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
      borderRadius: 'var(--r-pill)', background: s.bg, color: s.fg,
      font: 'var(--type-caption)', fontWeight: 500, whiteSpace: 'nowrap',
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: 999, background: s.dot, flexShrink: 0,
        animation: pulse ? 'vlpulse 1.2s var(--ease) infinite' : 'none',
      }} />
      {children}
    </span>
  );
}

// ─── カウントバッジ（未解決件数など）───
type CountKind = 'amber' | 'danger' | 'teal';
export function CountBadge({ count, kind = 'amber', icon }: { count?: number; kind?: CountKind; icon?: IconName }) {
  if (!count) return null;
  const map: Record<CountKind, { bg: string; fg: string }> = {
    amber: { bg: 'var(--cta-fill)', fg: 'var(--amber-400)' },
    danger: { bg: 'var(--danger-fill-strong)', fg: 'var(--danger)' },
    teal: { bg: 'var(--accent-fill)', fg: 'var(--teal-300)' },
  };
  const s = map[kind];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
      borderRadius: 'var(--r-pill)', background: s.bg, color: s.fg,
      font: 'var(--type-caption)', fontWeight: 600,
    }}>
      {icon && <Icon name={icon} size={13} />}
      {count}
    </span>
  );
}

// ─── 出典チップ（[日付] 会議名）───
export function SourceChip({ label, onClick, dimmed, ariaLabel }: { label: string; onClick?: () => void; dimmed?: boolean; ariaLabel?: string }) {
  return (
    <button onClick={onClick} aria-label={ariaLabel} title={onClick ? ariaLabel : undefined} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px 3px 8px',
      borderRadius: 'var(--r-1)', cursor: onClick ? 'pointer' : 'default',
      background: dimmed ? 'var(--overlay-faint)' : 'var(--accent-fill-faint)',
      border: '1px solid ' + (dimmed ? 'var(--border)' : 'var(--accent-line)'),
      color: dimmed ? 'var(--fg-tertiary)' : 'var(--teal-300)',
      font: 'var(--type-caption)', fontWeight: 500, whiteSpace: 'nowrap',
      transition: 'all var(--dur-fast) var(--ease)',
    }}
      onMouseEnter={(e) => { if (onClick && !dimmed) e.currentTarget.style.background = 'var(--accent-fill-hover)'; }}
      onMouseLeave={(e) => { if (onClick && !dimmed) e.currentTarget.style.background = 'var(--accent-fill-faint)'; }}>
      <Icon name="corner-down-right" size={12} />
      {label}
    </button>
  );
}

// ─── カード枠 ───
interface CardProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
  pad?: number;
  onClick?: () => void;
  hoverable?: boolean;
}
export function Card({ children, style, pad = 24, onClick, hoverable }: CardProps) {
  const [hover, setHover] = React.useState(false);
  return (
    <div onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        background: hoverable && hover ? 'var(--bg-hover)' : 'var(--bg-elevated)',
        borderRadius: 'var(--r-3)', padding: pad,
        border: '1px solid var(--border)', boxShadow: 'var(--shadow-1)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background var(--dur) var(--ease), border-color var(--dur) var(--ease)',
        borderColor: hoverable && hover ? 'var(--border-strong)' : 'var(--border)',
        ...style,
      }}>
      {children}
    </div>
  );
}

// ─── セクション見出し ───
interface SectionLabelProps {
  icon?: IconName;
  children: React.ReactNode;
  count?: React.ReactNode;
  right?: React.ReactNode;
}
export function SectionLabel({ icon, children, count, right }: SectionLabelProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
      {icon && <Icon name={icon} size={18} style={{ color: 'var(--accent)' }} />}
      <h3 style={{ font: 'var(--type-h3)', color: 'var(--fg)', fontWeight: 700, flex: count != null || right ? 'none' : 1 }}>
        {children}
      </h3>
      {count != null && (
        <span style={{ font: 'var(--type-caption)', color: 'var(--fg-tertiary)', fontWeight: 500 }}>{count}</span>
      )}
      <div style={{ flex: 1 }} />
      {right}
    </div>
  );
}

// ─── 期日のスタイル分け ───
type DueState = 'soon' | 'normal' | 'progress';
export function DueBadge({ due, state }: { due: string; state?: DueState }) {
  const map: Record<DueState, { fg: string; icon: IconName; spin?: boolean }> = {
    soon: { fg: 'var(--amber-400)', icon: 'clock' },
    normal: { fg: 'var(--fg-tertiary)', icon: 'calendar' },
    progress: { fg: 'var(--teal-300)', icon: 'loader', spin: true },
  };
  const s = map[state || 'normal'] || map.normal;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: s.fg, font: 'var(--type-caption)', fontWeight: 500 }}>
      <Icon name={s.icon} size={13} spin={s.spin} />
      {due}
    </span>
  );
}

// ─── ロック オーバーレイ（free/light 用 / 近日対応プレースホルダ） ───
export function LockVeil({ onUpsell, message, icon = 'lock', cta }: {
  onUpsell?: () => void;
  message?: string;
  icon?: IconName;
  cta?: string;
}) {
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 5,
      // 下層のぼかしプレビューが透けて見える軽いビネット。コンテンツ層が既に
      // blur 済みのため backdropFilter は二重ぼかし回避のため最小限（1px）。
      background: 'linear-gradient(180deg, var(--scrim-top), var(--scrim-bottom))',
      backdropFilter: 'blur(1px)', borderRadius: 'var(--r-3)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 12, padding: 24, textAlign: 'center',
    }}>
      {/* メッセージ + CTA はぼかしプレビュー上でも視認できるよう、不透明な小カードに載せる */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        padding: '18px 22px', borderRadius: 'var(--r-3)', maxWidth: 320,
        background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)',
        boxShadow: 'var(--shadow-1)',
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 999, display: 'grid', placeItems: 'center',
          background: 'var(--cta-fill)', color: 'var(--cta)',
        }}>
          <Icon name={icon} size={20} />
        </div>
        <div style={{ font: 'var(--type-ui)', color: 'var(--fg)', maxWidth: 280, lineHeight: 1.5 }}>
          {message || 'スタンダードでこの案件の全会議をAIに聞けます'}
        </div>
        {onUpsell && (
          <Btn kind="cta" size="sm" icon="sparkles" onClick={onUpsell}>{cta || 'スタンダードにする'}</Btn>
        )}
      </div>
    </div>
  );
}
